import i18next from 'i18next';
import { Instance, SnapshotOut, types } from 'mobx-state-tree';
import { LANGUAGE } from 'src/assets/static/constants';
import { ILoginData, IRegister } from 'src/screens';
import { authApi } from 'src/services';

import { withRootStore } from '../extensions/withRootStore';
import { withSetPropAction } from '../extensions/withSetPropAction';
import { IUser, User } from '../user/User';

/**
 * # AuthStore
 */
export const AuthStore = types
  .model('AuthStore')
  .props({
    isAuthenticated: types.optional(types.boolean, false),
    isInitialized: types.optional(types.boolean, false),
    user: types.optional(types.maybeNull(User), null),
    isSaved: types.optional(types.boolean, false),
  })
  .extend(withRootStore)
  .actions(withSetPropAction)
  .actions((self) => ({
    getAccessToken() {
      return localStorage.getItem('accessToken');
    },
    setAccessToken(token: string) {
      localStorage.setItem('accessToken', token);
    },
    getRefreshToken() {
      return localStorage.getItem('refreshToken');
    },
    setRefreshToken(token: string) {
      localStorage.setItem('refreshToken', token);
    },
  }))
  .actions((self) => ({
    initAuth() {
      self.setProp('isAuthenticated', false);
      self.setAccessToken('');
      self.setRefreshToken('');
    },
  }))
  .actions((self) => {
    /** 로그인 */
    const login = async (loginData: ILoginData) => {
      try {
        const result = await authApi.login(loginData);
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.access_token);
            self.setRefreshToken(oauthToken?.refresh_token);
          }
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
        console.error(error);
      }
    };

    /** 로그아웃 */
    const logout = async () => {
      try {
        const result = await authApi.logout();
        if (result?.resultCode === 'S') {
          self.initAuth();
          self.setProp('user', null);
          // 언어 설정은 유지 (로그아웃 후에도 선택한 언어로 표시)
        }
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    /** 내 정보 조회 */
    const getUserProfile = async () => {
      try {
        const result = await authApi.getUserProfile();
        if (result?.resultCode === 'S' && result.data) {
          const userProfile = result.data;
          if (userProfile) {
            self.setProp('user', userProfile);
            self.setProp('isAuthenticated', true);
            await i18next.changeLanguage(userProfile.langCd || LANGUAGE.korean);
          }
        } else {
          self.initAuth();
        }
        self.setProp('isInitialized', true);
        return result;
      } catch (error) {
        self.initAuth();
        console.error(error);
      }
    };

    /** 아이디 중복 확인 */
    const checkDuplicateLoginId = async (loginId: string) => {
      try {
        const result = await authApi.checkDuplicateLoginId(loginId);
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    /** 아이디 찾기 */
    const findLoginId = async (verifyKey: string) => {
      try {
        const result = await authApi.findLoginId(verifyKey);
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    /** 비밀번호 재설정(로그인 이전) */
    const resetPwd = async (user: IUser) => {
      try {
        const result = await authApi.resetPwd(user);
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    /** 토큰 재발급 */
    const loginRefresh = async (refreshToken: string) => {
      try {
        const result = await authApi.loginRefresh(refreshToken);
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    /** 비밀번호 변경(로그인 이후) */
    const changePwd = async (user: IUser) => {
      try {
        const result = await authApi.changePwd(user);
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    /** 회원가입 */
    const signup = async (formData: IRegister) => {
      try {
        const result = await authApi.signup(formData);
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.access_token);
            self.setRefreshToken(oauthToken?.refresh_token);
          }
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
        console.error(error);
      }
    };

    /** 언어 변경 */
    const changeLanguage = async (langCd: string) => {
      try {
        const result = await authApi.changeLanguage(langCd);
        if (result?.resultCode === 'S' && result.data) {
          const oauthToken = result.data;
          if (oauthToken) {
            self.setAccessToken(oauthToken?.access_token);
            self.setRefreshToken(oauthToken?.refresh_token);
          }
        } else {
          self.initAuth();
        }
        return result;
      } catch (error) {
        self.initAuth();
        console.error(error);
      }
    };

    /** 회원탈퇴 */
    const withdrawal = async () => {
      try {
        const result = await authApi.withdrawal();
        if (result?.resultCode === 'S') {
          self.initAuth();
          self.setProp('user', null);
          // 언어 설정은 유지 (탈퇴 후에도 선택한 언어로 표시)
        }
        return result;
      } catch (error) {
        console.error(error);
      }
    };

    return {
      login,
      logout,
      getUserProfile,
      checkDuplicateLoginId,
      findLoginId,
      resetPwd,
      loginRefresh,
      changePwd,
      signup,
      changeLanguage,
      withdrawal,
    };
  });

type TAuthStore = Instance<typeof AuthStore>;
type TAuthStoreSnapshot = SnapshotOut<typeof AuthStore>;
export interface IAuthStore extends TAuthStore {}
export type TAuthStoreKeys = keyof TAuthStoreSnapshot & string;
export interface IAuthStoreSnapshot extends TAuthStoreSnapshot {}
